/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, takeLeading, select, race, take, call, all, takeLatest, fork } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ParentalRatingValue,
  Time,
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  User,
  Role,
  PinErrors,
  Specs,
  UpdatablePreference,
  ParentalControlSettings,
  Ducks,
  LocalStorage,
  ActionsEventActionNames,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import * as PinDuck from 'store/ducks/settings/managePin';
import { addPinToggleActionEvent, addSettingsActionEvent } from '../../../store/ducks/analytics/actionsEvent';

export enum ActionTypes {
  CHANGE_MASTER_PIN = 'parentalControl/preference/CHANGE_MASTER_PIN',
  CHANGE_PARENTAL_VALIDATION_REQUEST = 'parentalControl/preference/CHANGE_PARENTAL_VALIDATION_REQUEST',
  CHANGE_PARENTAL_VALIDATION_SUCCESS = 'parentalControl/preference/CHANGE_PARENTAL_VALIDATION_SUCCESS',
  CHANGE_PARENTAL_VALIDATION_FAIL = 'parentalControl/preference/CHANGE_PARENTAL_VALIDATION_FAIL',
  CHANGE_MASTER_PIN_SUCCESS = 'parentalControl/preference/CHANGE_MASTER_PIN_SUCCESS',
  CHANGE_MASTER_PIN_FAIL = 'parentalControl/preference/CHANGE_MASTER_PIN_FAIL',
  CHANGE_LOCKED_CHANNELS = 'parentalControl/CHANGE_LOCKED_CHANNELS',
  CHANGE_LOCKED_CHANNELS_SUCCESS = 'parentalControl/CHANGE_LOCKED_CHANNELS_SUCCESS',
  CHANGE_LOCKED_CHANNELS_FAIL = 'parentalControl/CHANGE_LOCKED_CHANNELS_FAIL',
  CHANGE_PURCHASE_PIN = 'parentalControl/CHANGE_PURCHASE_PIN',
  CHANGE_PURCHASE_PIN_SUCCESS = 'parentalControl/CHANGE_PURCHASE_PIN_SUCCESS',
  CHANGE_PURCHASE_PIN_ERROR = 'parentalControl/CHANGE_PURCHASE_PIN_ERROR',
  LOAD_LOCKED_CHANNELS = 'parentalControl/LOAD_LOCKED_CHANNELS',
  LOAD_LOCKED_CHANNELS_SUCCESS = 'parentalControl/LOAD_LOCKED_CHANNELS_SUCCESS',
  LOAD_LOCKED_CHANNELS_FAIL = 'parentalControl/LOAD_LOCKED_CHANNELS_FAIL',
  LOAD_AGE_RATING = 'parentalControl/LOAD_AGE_RATING',
  LOAD_AGE_RATING_SUCCESS = 'parentalControl/LOAD_AGE_RATING_SUCCESS',
  LOAD_AGE_RATING_FAIL = 'parentalControl/LOAD_AGE_RATING_FAIL',
  SET_AGE_RATING = 'parentalControl/SET_AGE_RATING',
  SET_AGE_RATING_SUCCESS = 'parentalControl/SET_AGE_RATING_SUCCESS',
  SET_AGE_RATING_FAIL = 'parentalControl/SET_AGE_RATING_FAIL',
  SET_AGE_RATING_ASK_PIN = 'parentalControl/SET_AGE_RATING_ASK_PIN',
  SET_PARENTAL_LOCK_OCCURRED = 'parentalControl/SET_PARENTAL_LOCK_OCCURRED',
  ACCESSIBILITY_SUBTITLES_REQUEST = 'parentalControl/ACCESSIBILITY_SUBTITLES_REQUEST',
  ACCESSIBILITY_SUBTITLES_SUCCESS = 'parentalControl/ACCESSIBILITY_SUBTITLES_SUCCESS',
  ACCESSIBILITY_SUBTITLES_FAIL = 'parentalControl/ACCESSIBILITY_SUBTITLES_FAIL',
  ACCESSIBILITY_AUDIO_DESCRIPTION_REQUEST = 'parentalControl/ACCESSIBILITY_AUDIO_DESCRIPTION_REQUEST',
  ACCESSIBILITY_AUDIO_DESCRIPTION_SUCCESS = 'parentalControl/ACCESSIBILITY_AUDIO_DESCRIPTION_SUCCESS',
  ACCESSIBILITY_AUDIO_DESCRIPTION_FAIL = 'parentalControl/ACCESSIBILITY_AUDIO_DESCRIPTION_FAIL',
  RESET_ERRORS = 'parentalControl/RESET_ERRORS',
}

interface ChangeMasterPinMeta {
  masterPin: string;
}

interface ChangeParentalValidationMeta {
  parentalValidation: boolean;
}

interface ChangePurchasePinMeta {
  purchasePinEnabled: boolean;
}

interface ChangeAccessibilitySubtitlesMeta {
  accessibilitySubtitles?: boolean;
}

interface ChangeAccessibilityAudioDescriptionMeta {
  accessibilityAudioDescription: boolean;
}

interface LockedChannelsPayload {
  lockedChannelsList: number[];
}

interface ageRatingMeta {
  ageRatingValue: number | undefined;
}

interface AgeRatingPayload {
  ageRatingValue: ParentalRatingValue | undefined;
}

interface AgeRatingAskPinPayload {
  ageRatingAskPin: boolean;
}

interface SetParentalLockOccurredMeta {
  parentalLockOccurred: Time;
}

type ChangeMasterPin = ActionRequest<ActionTypes.CHANGE_MASTER_PIN, ChangeMasterPinMeta>;
type LoadAgeRating = ActionRequest<ActionTypes.LOAD_AGE_RATING>;
type LoadLockedChannels = ActionRequest<ActionTypes.LOAD_LOCKED_CHANNELS>;
type LoadLockedChannelsSuccess = ActionSuccess<ActionTypes.LOAD_LOCKED_CHANNELS_SUCCESS, LockedChannelsPayload>;
type LoadLockedChannelsFail = ActionFail<ActionTypes.LOAD_LOCKED_CHANNELS_FAIL>;
type SetAgeRating = ActionRequest<ActionTypes.SET_AGE_RATING, ageRatingMeta>;
type SetAgeRatingSuccess = ActionSuccess<ActionTypes.SET_AGE_RATING_SUCCESS, AgeRatingPayload>;
type SetAgeRatingFail = ActionFail<ActionTypes.SET_AGE_RATING_FAIL>;
type SetAgeRatingAskPin = ActionSuccess<ActionTypes.SET_AGE_RATING_ASK_PIN, AgeRatingAskPinPayload>;
type LoadAgeRatingSuccess = ActionSuccess<ActionTypes.LOAD_AGE_RATING_SUCCESS, AgeRatingPayload>;
type LoadAgeRatingFailed = ActionFail<ActionTypes.LOAD_AGE_RATING_FAIL>;
type ChangeMasterPinSuccess = ActionSuccess<ActionTypes.CHANGE_MASTER_PIN_SUCCESS>;
type ChangeMasterPinFailed = ActionFail<ActionTypes.CHANGE_MASTER_PIN_FAIL>;
type ChangeParentalValidation = ActionRequest<
  ActionTypes.CHANGE_PARENTAL_VALIDATION_REQUEST,
  ChangeParentalValidationMeta
>;
type ChangeParentalValidationSuccess = ActionSuccess<ActionTypes.CHANGE_PARENTAL_VALIDATION_SUCCESS>;
type ChangeParentalValidationFailed = ActionFail<ActionTypes.CHANGE_PARENTAL_VALIDATION_FAIL>;
type ChangePurchasePin = ActionRequest<ActionTypes.CHANGE_PURCHASE_PIN, ChangePurchasePinMeta>;
type ChangePurchasePinSuccess = ActionSuccess<ActionTypes.CHANGE_PURCHASE_PIN_SUCCESS>;
type ChangePurchasePinFailed = ActionFail<ActionTypes.CHANGE_PURCHASE_PIN_ERROR>;
type ChangeAccessibilitySubtitlesRequest = ActionRequest<
  ActionTypes.ACCESSIBILITY_SUBTITLES_REQUEST,
  ChangeAccessibilitySubtitlesMeta
>;
type ChangeAccessibilitySubtitlesSuccess = ActionSuccess<ActionTypes.ACCESSIBILITY_SUBTITLES_SUCCESS>;
type ChangeAccessibilitySubtitlesFailed = ActionFail<ActionTypes.ACCESSIBILITY_SUBTITLES_FAIL>;
type ChangeAccessibilityAudioDescriptionRequest = ActionRequest<
  ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_REQUEST,
  ChangeAccessibilityAudioDescriptionMeta
>;
type ChangeAccessibilityAudioDescriptionSuccess = ActionSuccess<ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_SUCCESS>;
type ChangeAccessibilityAudioDescriptionFailed = ActionFail<ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_FAIL>;
type SetParentalLockOccurred = ActionRequest<ActionTypes.SET_PARENTAL_LOCK_OCCURRED, SetParentalLockOccurredMeta>;
type ChangeLockedChannels = ActionRequest<ActionTypes.CHANGE_LOCKED_CHANNELS, LockedChannelsPayload>;
type ChangeLockedChannelsSuccess = ActionSuccess<ActionTypes.CHANGE_LOCKED_CHANNELS_SUCCESS, LockedChannelsPayload>;
type ChangeLockedChannelsFail = ActionFail<ActionTypes.CHANGE_LOCKED_CHANNELS_FAIL>;
type ResetErrors = ActionRequest<ActionTypes.RESET_ERRORS>;
type Actions =
  | ChangeMasterPin
  | ChangeMasterPinSuccess
  | ChangeMasterPinFailed
  | ChangeParentalValidation
  | ChangeParentalValidationFailed
  | ChangeParentalValidationSuccess
  | ChangePurchasePin
  | ChangePurchasePinSuccess
  | ChangePurchasePinFailed
  | ChangeLockedChannels
  | ChangeLockedChannelsSuccess
  | ChangeLockedChannelsFail
  | LoadLockedChannels
  | LoadLockedChannelsSuccess
  | LoadLockedChannelsFail
  | LoadAgeRating
  | LoadAgeRatingSuccess
  | LoadAgeRatingFailed
  | SetAgeRating
  | SetAgeRatingSuccess
  | SetAgeRatingFail
  | SetAgeRatingAskPin
  | ChangeAccessibilitySubtitlesRequest
  | ChangeAccessibilitySubtitlesSuccess
  | ChangeAccessibilitySubtitlesFailed
  | ChangeAccessibilityAudioDescriptionRequest
  | ChangeAccessibilityAudioDescriptionSuccess
  | ChangeAccessibilityAudioDescriptionFailed
  | SetParentalLockOccurred
  | ResetErrors;

const Actions = {
  changeMasterPin: (masterPin: string): ChangeMasterPin => ({
    type: ActionTypes.CHANGE_MASTER_PIN,
    meta: { masterPin },
  }),
  changeParentalValidation: (parentalValidation: boolean): ChangeParentalValidation => ({
    type: ActionTypes.CHANGE_PARENTAL_VALIDATION_REQUEST,
    meta: { parentalValidation },
  }),
  changePurchasePin: (purchasePinEnabled: boolean): ChangePurchasePin => ({
    type: ActionTypes.CHANGE_PURCHASE_PIN,
    meta: { purchasePinEnabled },
  }),
  changePurchasePinSuccess: (): ChangePurchasePinSuccess => ({ type: ActionTypes.CHANGE_PURCHASE_PIN_SUCCESS }),
  changePurchasePinFail: (error: XError): ChangePurchasePinFailed => ({
    type: ActionTypes.CHANGE_PURCHASE_PIN_ERROR,
    error,
  }),
  changeMasterPinSuccess: (): ChangeMasterPinSuccess => ({ type: ActionTypes.CHANGE_MASTER_PIN_SUCCESS }),
  changeMasterPinFail: (error: XError): ChangeMasterPinFailed => ({
    type: ActionTypes.CHANGE_MASTER_PIN_FAIL,
    error,
  }),
  changeParentalValidationSuccess: (): ChangeParentalValidationSuccess => ({
    type: ActionTypes.CHANGE_PARENTAL_VALIDATION_SUCCESS,
  }),
  changeParentalValidationFail: (error: XError): ChangeParentalValidationFailed => ({
    type: ActionTypes.CHANGE_PARENTAL_VALIDATION_FAIL,
    error,
  }),
  changeAccessibilitySubtitlesRequest: (accessibilitySubtitles: boolean): ChangeAccessibilitySubtitlesRequest => ({
    type: ActionTypes.ACCESSIBILITY_SUBTITLES_REQUEST,
    meta: { accessibilitySubtitles },
  }),
  changeAccessibilitySubtitlesSuccess: (): ChangeAccessibilitySubtitlesSuccess => ({
    type: ActionTypes.ACCESSIBILITY_SUBTITLES_SUCCESS,
  }),
  changeAccessibilitySubtitlesFail: (error: XError): ChangeAccessibilitySubtitlesFailed => ({
    type: ActionTypes.ACCESSIBILITY_SUBTITLES_FAIL,
    error,
  }),
  changeAccessibilityAudioDescriptionRequest: (
    accessibilityAudioDescription: boolean
  ): ChangeAccessibilityAudioDescriptionRequest => ({
    type: ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_REQUEST,
    meta: { accessibilityAudioDescription },
  }),
  changeAccessibilityAudioDescriptionSuccess: (): ChangeAccessibilityAudioDescriptionSuccess => ({
    type: ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_SUCCESS,
  }),
  changeAccessibilityAudioDescriptionFail: (error: XError): ChangeAccessibilityAudioDescriptionFailed => ({
    type: ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_FAIL,
    error,
  }),
  changeLockedChannels: (lockedChannelsList: number[]): ChangeLockedChannels => ({
    type: ActionTypes.CHANGE_LOCKED_CHANNELS,
    meta: { lockedChannelsList },
  }),
  changeLockedChannelsSuccess: (lockedChannelsList: number[]): ChangeLockedChannelsSuccess => ({
    type: ActionTypes.CHANGE_LOCKED_CHANNELS_SUCCESS,
    payload: { lockedChannelsList },
  }),
  changeLockedChannelsFail: (error: XError): ChangeLockedChannelsFail => ({
    type: ActionTypes.CHANGE_LOCKED_CHANNELS_FAIL,
    error,
  }),
  loadLockedChannels: (): LoadLockedChannels => ({
    type: ActionTypes.LOAD_LOCKED_CHANNELS,
  }),
  loadLockedChannelsSuccess: (lockedChannelsList: number[]): LoadLockedChannelsSuccess => ({
    type: ActionTypes.LOAD_LOCKED_CHANNELS_SUCCESS,
    payload: { lockedChannelsList },
  }),
  loadLockedChannelsFail: (error: XError): LoadLockedChannelsFail => ({
    type: ActionTypes.LOAD_LOCKED_CHANNELS_FAIL,
    error,
  }),
  loadAgeRatingFailed: (error: XError): LoadAgeRatingFailed => ({
    type: ActionTypes.LOAD_AGE_RATING_FAIL,
    error,
  }),
  loadAgeRatingSuccess: (ageRatingValue: ParentalRatingValue | undefined): LoadAgeRatingSuccess => ({
    type: ActionTypes.LOAD_AGE_RATING_SUCCESS,
    payload: { ageRatingValue },
  }),
  loadAgeRating: (): LoadAgeRating => ({ type: ActionTypes.LOAD_AGE_RATING }),
  setAgeRating: (ageRatingValue: number | undefined): SetAgeRating => ({
    type: ActionTypes.SET_AGE_RATING,
    meta: { ageRatingValue },
  }),
  setAgeRatingSuccess: (ageRatingValue: ParentalRatingValue | undefined): SetAgeRatingSuccess => ({
    type: ActionTypes.SET_AGE_RATING_SUCCESS,
    payload: { ageRatingValue },
  }),
  setAgeRatingAskPin: (ageRatingAskPin: boolean): SetAgeRatingAskPin => ({
    type: ActionTypes.SET_AGE_RATING_ASK_PIN,
    payload: { ageRatingAskPin },
  }),
  setAgeRatingFail: (error: XError): SetAgeRatingFail => ({
    type: ActionTypes.SET_AGE_RATING_FAIL,
    error,
  }),
  setParentalLockOccurred: (parentalLockOccurred: Time): SetParentalLockOccurred => ({
    type: ActionTypes.SET_PARENTAL_LOCK_OCCURRED,
    meta: { parentalLockOccurred },
  }),
  resetErrors: () => ({
    type: ActionTypes.RESET_ERRORS,
  }),
};

export const ActionCreators = {
  changeMasterPin: Actions.changeMasterPin,
  changeParentalValidation: Actions.changeParentalValidation,
  changePurchasePin: Actions.changePurchasePin,
  changeAccessibilitySubtitles: Actions.changeAccessibilitySubtitlesRequest,
  changeAccessibilityAudioDescription: Actions.changeAccessibilityAudioDescriptionRequest,
  setAgeRating: Actions.setAgeRating,
  setAgeRatingAskPin: Actions.setAgeRatingAskPin,
  setParentalLockOccurred: Actions.setParentalLockOccurred,
  changeLockedChannels: Actions.changeLockedChannels,
  loadAgeRating: Actions.loadAgeRating,
  resetErrors: Actions.resetErrors,
};

export interface ReducerState {
  parentalLockOccurred?: Time;
  ageRating?: ParentalRatingValue;
  ageRatingAskPin: boolean;
  lockedChannels: number[];
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

// REDUCERS
const initialState: ReducerState = {
  processing: false,
  processed: false,
  error: undefined,
  ageRating: undefined,
  ageRatingAskPin: true,
  parentalLockOccurred: undefined,
  lockedChannels: [],
};

export default function parentalControl(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.RESET_ERRORS:
      return {
        ...state,
        error: undefined,
      };
    case ActionTypes.CHANGE_PARENTAL_VALIDATION_REQUEST:
    case ActionTypes.SET_AGE_RATING:
    case ActionTypes.LOAD_AGE_RATING:
    case ActionTypes.CHANGE_MASTER_PIN:
    case ActionTypes.CHANGE_LOCKED_CHANNELS:
    case ActionTypes.LOAD_LOCKED_CHANNELS:
    case ActionTypes.CHANGE_PURCHASE_PIN:
    case ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_REQUEST:
    case ActionTypes.ACCESSIBILITY_SUBTITLES_REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
        error: undefined,
      };
    case ActionTypes.CHANGE_PARENTAL_VALIDATION_SUCCESS:
    case ActionTypes.CHANGE_MASTER_PIN_SUCCESS:
    case ActionTypes.CHANGE_PURCHASE_PIN_SUCCESS:
    case ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_SUCCESS:
    case ActionTypes.ACCESSIBILITY_SUBTITLES_SUCCESS:
      return {
        ...state,
        processing: false,
        processed: true,
      };
    case ActionTypes.SET_AGE_RATING_SUCCESS:
    case ActionTypes.LOAD_AGE_RATING_SUCCESS:
      return {
        ...state,
        processing: false,
        processed: true,
        ageRating: action.payload.ageRatingValue,
        parentalLockOccurred: undefined, // clear the parentalLockOccurred to reset the grace period
      };
    case ActionTypes.SET_AGE_RATING_ASK_PIN:
      return {
        ...state,
        ageRatingAskPin: action.payload.ageRatingAskPin,
      };
    case ActionTypes.SET_PARENTAL_LOCK_OCCURRED:
      return {
        ...state,
        parentalLockOccurred: action.meta.parentalLockOccurred,
      };
    case ActionTypes.CHANGE_LOCKED_CHANNELS_SUCCESS:
    case ActionTypes.LOAD_LOCKED_CHANNELS_SUCCESS:
      return {
        ...state,
        processing: false,
        processed: true,
        lockedChannels: action.payload.lockedChannelsList,
      };
    case ActionTypes.CHANGE_PARENTAL_VALIDATION_FAIL:
    case ActionTypes.SET_AGE_RATING_FAIL:
    case ActionTypes.LOAD_AGE_RATING_FAIL:
    case ActionTypes.CHANGE_MASTER_PIN_FAIL:
    case ActionTypes.CHANGE_LOCKED_CHANNELS_FAIL:
    case ActionTypes.LOAD_LOCKED_CHANNELS_FAIL:
    case ActionTypes.CHANGE_PURCHASE_PIN_ERROR:
    case ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_FAIL:
    case ActionTypes.ACCESSIBILITY_SUBTITLES_FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    default:
      return state;
  }
}

export const Selectors = {
  isProcessed: (state: StoreState): boolean => state.parentalControl.processed === true,
  isProcessing: (state: StoreState): boolean => state.parentalControl.processing === true,
  getError: (state: StoreState): XError | undefined => state.parentalControl.error,
  getAgeRatings: (state: StoreState): ParentalRatingValue[] | undefined =>
    state.configs.dms.data?.parentalControl.parentalRating.parentalRatingValues.filter((age) => age.selectable),
  getAgeRatingAskPin: (state: StoreState): boolean => state.parentalControl.ageRatingAskPin,
  getAgeRating: (state: StoreState): ParentalRatingValue | undefined => state.parentalControl.ageRating,
  getParentalLockOccurred: (state: StoreState): Time | undefined => state.parentalControl.parentalLockOccurred,
  getLockedChannels: (state: StoreState): number[] => state.parentalControl.lockedChannels,
};

// SAGAS
function* changeMasterPinSaga(action: ChangeMasterPin) {
  try {
    const users: User[] = yield select(Ducks.Household.Selectors.data.getUsers);
    const defaultUser = users.find((user) => user.role === Role.DEFAULT) as User;
    const masterPinToChange: string = action.meta.masterPin;

    // PIN_IMP02.004
    /*
    if (action.meta.masterPin.every((val, _i, arr) => val === arr[0])) {
      yield put(Actions.changeMasterPinFail(XError.fromCode(PinErrors.generic.consecutiveDigitsMasterPin)));
      return;
    }
    */
    // PIN_IMP02.005
    if (masterPinToChange === Specs.DEFAULT_MASTER_PIN) {
      throw XError.fromCode(PinErrors.generic.defaultMasterPin);
    } else {
      yield put(
        Ducks.Household.Creators.updateUserPreference(
          { ...defaultUser, [UpdatablePreference.MASTER_PIN]: masterPinToChange },
          UpdatablePreference.MASTER_PIN
        )
      );

      const { resultSuccess } = yield race({
        resultSuccess: take(Ducks.Household.ActionType.updateUserPreference.SUCCESS),
        resultError: take(Ducks.Household.ActionType.updateUserPreference.FAIL),
      });

      if (resultSuccess) {
        yield put(Actions.changeMasterPinSuccess());
        yield fork(addSettingsActionEvent, ActionsEventActionNames.CHANGE_PIN);
      } else {
        throw XError.fromCode(PinErrors.generic.savingMasterPin);
      }
    }
  } catch (e) {
    const error = XError.fromError(e);
    yield put(Actions.changeMasterPinFail(error));
    yield put(PinDuck.ActionCreators.pinError(error));
    yield fork(addSettingsActionEvent, ActionsEventActionNames.CHANGE_PIN, undefined, undefined, error);
  }
}

function* changeParentalValidationSaga(action: ChangeParentalValidation) {
  const { parentalValidation } = action.meta;
  try {
    const user: User | undefined = yield select(Ducks.Household.Selectors.data.getDefaultUser);
    if (!user) {
      yield put(
        Actions.changeParentalValidationFail(XError.fromError(new Error('Error on change parental validation')))
      );
      return;
    }
    yield put(
      Ducks.Household.Creators.updateUserPreference(
        { ...user, [UpdatablePreference.PARENTAL_VALIDATION]: parentalValidation },
        UpdatablePreference.PARENTAL_VALIDATION
      )
    );

    const { resultSuccess } = yield race({
      resultSuccess: take(Ducks.Household.ActionType.updateUserPreference.SUCCESS),
      resultError: take(Ducks.Household.ActionType.updateUserPreference.FAIL),
    });

    if (resultSuccess) {
      yield put(Actions.changeParentalValidationSuccess());
      yield fork(addPinToggleActionEvent, parentalValidation);
    } else {
      const error = XError.fromError(new Error('Error on change parental validation'));
      yield put(Actions.changeParentalValidationFail(error));
      yield fork(addPinToggleActionEvent, parentalValidation, error);
    }
  } catch (e) {
    const error = XError.fromError(e);
    yield put(Actions.changeParentalValidationFail(XError.fromError(error)));
    yield fork(addPinToggleActionEvent, parentalValidation, error);
  }
}

function* changePurchasePinSaga(action: ChangePurchasePin) {
  try {
    const user: User | undefined = yield select(Ducks.Household.Selectors.data.getDefaultUser);

    if (!user) {
      throw XError.fromError(new Error('Error on change parental validation'));
    }

    yield put(
      Ducks.Household.Creators.updateUserPreference(
        { ...user, [UpdatablePreference.PURCHASE_PIN_ENABLED]: action.meta.purchasePinEnabled },
        UpdatablePreference.PURCHASE_PIN_ENABLED
      )
    );

    const { resultSuccess } = yield race({
      resultSuccess: take(Ducks.Household.ActionType.updateUserPreference.SUCCESS),
      resultError: take(Ducks.Household.ActionType.updateUserPreference.FAIL),
    });

    if (resultSuccess) {
      yield put(Actions.changePurchasePinSuccess());
      yield fork(addSettingsActionEvent, ActionsEventActionNames.CHANGE_PIN);
    } else {
      throw XError.fromError(new Error('Error on change parental validation'));
    }
  } catch (e) {
    const error = XError.fromError(e);
    yield put(Actions.changePurchasePinFail(error));
    yield fork(addSettingsActionEvent, ActionsEventActionNames.CHANGE_PIN, undefined, undefined, error);
  }
}

function* changeAccessibilitySubtitlesSaga(action: ChangeAccessibilitySubtitlesRequest) {
  try {
    const users: User[] = yield select(Ducks.Household.Selectors.data.getUsers);
    const currentUserId: string = yield select(Ducks.Credentials.Selectors.getUserId);
    const user = users.find((cur) => cur.id === currentUserId);

    if (!user) {
      throw XError.fromError(new Error('Error on change AccessibilitySubtitles'));
    }

    yield put(
      Ducks.Household.Creators.updateUserPreference(
        { ...user, [UpdatablePreference.ACCESSIBILITY_SUBTITLES]: action.meta.accessibilitySubtitles },
        UpdatablePreference.ACCESSIBILITY_SUBTITLES
      )
    );

    const { resultSuccess } = yield race({
      resultSuccess: take(Ducks.Household.ActionType.updateUserPreference.SUCCESS),
      resultError: take(Ducks.Household.ActionType.updateUserPreference.FAIL),
    });

    if (resultSuccess) {
      yield put(Actions.changeAccessibilitySubtitlesSuccess());
      yield fork(
        addSettingsActionEvent,
        ActionsEventActionNames.SUBTITLES,
        action.meta.accessibilitySubtitles !== undefined ? action.meta.accessibilitySubtitles.toString() : undefined
      );
    } else {
      throw XError.fromError(new Error('Error on change AccessibilitySubtitles'));
    }
  } catch (e) {
    const error = XError.fromError(e);
    yield put(Actions.changeAccessibilitySubtitlesFail(error));
    yield fork(
      addSettingsActionEvent,
      ActionsEventActionNames.SUBTITLES,
      action.meta.accessibilitySubtitles !== undefined ? action.meta.accessibilitySubtitles.toString() : undefined,
      undefined,
      error
    );
  }
}

function* changeAccessibilityAudioDescriptionSaga(action: ChangeAccessibilityAudioDescriptionRequest) {
  try {
    const users: User[] = yield select(Ducks.Household.Selectors.data.getUsers);
    const currentUserId: string = yield select(Ducks.Credentials.Selectors.getUserId);
    const user = users.find((cur) => cur.id === currentUserId);

    if (!user) {
      throw XError.fromError(new Error('Error on change AudioDescription'));
    }

    yield put(
      Ducks.Household.Creators.updateUserPreference(
        { ...user, [UpdatablePreference.ACCESSIBILITY_AUDIO_DESCRIPTION]: action.meta.accessibilityAudioDescription },
        UpdatablePreference.ACCESSIBILITY_AUDIO_DESCRIPTION
      )
    );

    const { resultSuccess } = yield race({
      resultSuccess: take(Ducks.Household.ActionType.updateUserPreference.SUCCESS),
      resultError: take(Ducks.Household.ActionType.updateUserPreference.FAIL),
    });

    if (resultSuccess) {
      yield put(Actions.changeAccessibilityAudioDescriptionSuccess());
      yield fork(
        addSettingsActionEvent,
        ActionsEventActionNames.AUDIO_DESCRIPTION,
        action.meta.accessibilityAudioDescription.toString()
      );
    } else {
      throw XError.fromError(new Error('Error on change AudioDescription'));
    }
  } catch (e) {
    const error = XError.fromError(e);
    yield put(Actions.changeAccessibilityAudioDescriptionFail(error));
    yield fork(
      addSettingsActionEvent,
      ActionsEventActionNames.AUDIO_DESCRIPTION,
      action.meta.accessibilityAudioDescription.toString(),
      undefined,
      error
    );
  }
}

function* getAgeRatingFromStorage(): SagaIterator {
  try {
    return yield call(LocalStorage.getAgeRating);
  } catch {
    return undefined;
  }
}

function* setAgeRatingToStorage(ratingValue: number | undefined): SagaIterator {
  yield call(LocalStorage.storeAgeRating, ratingValue || 0);
}

function* loadAgeRatingSaga() {
  try {
    const ratingValueStorage: number | undefined = yield call(getAgeRatingFromStorage);

    if (ratingValueStorage === 0) {
      // age ratting was disabled
      yield put(Actions.loadAgeRatingSuccess(undefined));
      return;
    }

    const pc: ParentalControlSettings | undefined = yield select(Ducks.Dms.Selectors.getParentalControl);

    let ageRatingFromStore: ParentalRatingValue | undefined;
    if (ratingValueStorage) {
      ageRatingFromStore = pc?.parentalRating.parentalRatingValues.find(
        (ratingValue) => ratingValue.selectable && ratingValue.ratingValue === ratingValueStorage
      );
    }
    if (!ageRatingFromStore) {
      ageRatingFromStore = pc?.parentalRating.defaultSetting;
    }

    if (ageRatingFromStore) {
      yield put(Actions.loadAgeRatingSuccess(ageRatingFromStore));
    } else {
      yield put(Actions.loadAgeRatingFailed(XError.fromError(new Error('Error on load age rating.'))));
    }
  } catch (error) {
    yield put(Actions.loadAgeRatingFailed(XError.fromError(error)));
  }
}

function* setAgeRatingSaga(action: SetAgeRating) {
  const currentAgeRating: ParentalRatingValue | undefined = yield select(Selectors.getAgeRating);
  const oldValue: string | undefined = currentAgeRating?.label;
  let newValue: string | undefined;
  try {
    const pc: ParentalControlSettings | undefined = yield select(Ducks.Dms.Selectors.getParentalControl);
    let ageRatingToStore: ParentalRatingValue | undefined;
    if (action.meta.ageRatingValue) {
      ageRatingToStore = pc?.parentalRating.parentalRatingValues.find(
        (age) => age.selectable && age.ratingValue === action.meta.ageRatingValue
      );
    }

    newValue = ageRatingToStore?.label;
    yield call(setAgeRatingToStorage, ageRatingToStore?.ratingValue);
    yield put(Actions.setAgeRatingSuccess(ageRatingToStore));
    yield fork(addSettingsActionEvent, ActionsEventActionNames.PARENTAL_CONTROL, newValue, oldValue);
  } catch (e) {
    const error = XError.fromError(e);
    yield put(Actions.setAgeRatingFail(error));
    yield fork(addSettingsActionEvent, ActionsEventActionNames.PARENTAL_CONTROL, newValue, oldValue, error);
  }
}

function* getLockedChannelsFromCache(): SagaIterator<number[] | undefined> {
  try {
    return yield call(LocalStorage.getLockedChannels);
  } catch {
    return undefined;
  }
}

function* setLockedChannelsToStorage(lockedChannelsList: number[]): SagaIterator {
  yield call(LocalStorage.storeLockedChannels, lockedChannelsList);
}

function* changeLockedChannelsSaga(action: ChangeLockedChannels): SagaIterator {
  try {
    const { lockedChannelsList } = action.meta;
    yield call(setLockedChannelsToStorage, lockedChannelsList);
    yield put(Actions.changeLockedChannelsSuccess(lockedChannelsList));
  } catch (error) {
    yield put(Actions.changeLockedChannelsFail(XError.fromError(error)));
  }
}

function* loadLockedChannelsSaga(): SagaIterator<void> {
  const lockedChannels: number[] | undefined = yield call(getLockedChannelsFromCache);
  yield put(Actions.loadLockedChannelsSuccess(lockedChannels || []));
}

function* initSaga(): SagaIterator {
  try {
    yield put(Actions.loadAgeRating());
  } catch (error) {
    yield put(Actions.loadAgeRatingFailed(XError.fromError(error)));
  }
  try {
    yield put(Actions.loadLockedChannels());
  } catch (error) {
    yield put(Actions.loadLockedChannelsFail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([
    takeLeading(ActionTypes.CHANGE_MASTER_PIN, changeMasterPinSaga),
    takeLeading(ActionTypes.CHANGE_PARENTAL_VALIDATION_REQUEST, changeParentalValidationSaga),
    takeLeading(ActionTypes.CHANGE_PURCHASE_PIN, changePurchasePinSaga),
    takeLeading(ActionTypes.ACCESSIBILITY_AUDIO_DESCRIPTION_REQUEST, changeAccessibilityAudioDescriptionSaga),
    takeLeading(ActionTypes.ACCESSIBILITY_SUBTITLES_REQUEST, changeAccessibilitySubtitlesSaga),
    takeLatest(Ducks.Configs.ActionTypes.SUCCESS, initSaga),
    takeLeading(ActionTypes.LOAD_AGE_RATING, loadAgeRatingSaga),
    takeLeading(ActionTypes.SET_AGE_RATING, setAgeRatingSaga),
    takeLeading(ActionTypes.LOAD_LOCKED_CHANNELS, loadLockedChannelsSaga),
    takeLeading(ActionTypes.CHANGE_LOCKED_CHANNELS, changeLockedChannelsSaga),
  ]);
}
